import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button,
    List, ListItem, ListItemText, ListItemSecondaryAction,
    IconButton, Typography, CircularProgress, Box
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import { API_BASE_URL } from '../../../App';

const EventBookingsModal = ({ isOpen, onClose, event }) => {
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(false);

    const fetchBookings = useCallback(async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/api/events/${event._id}/bookings`);
            setBookings(response.data);
        } catch (error) {
            toast.error('Fehler beim Laden der Termine.');
        } finally {
            setLoading(false);
        }
    }, [event?._id]);

    useEffect(() => {
        if (event && isOpen) {
            fetchBookings();
        }
    }, [event, isOpen, fetchBookings]);

    const handleToggleStatus = async (booking) => {
        const newStatus = booking.status === 0 ? 1 : 0; // Toggle between 0 (active) and 1 (cancelled)
        try {
            await axios.put(`${API_BASE_URL}/api/bookings/${booking._id}`, {
                status: newStatus
            });
            
            toast.success(newStatus === 1 ? 'Termin deaktiviert' : 'Termin aktiviert');
            fetchBookings(); // Refresh list
        } catch (error) {
            const errorMsg = error.response?.data?.message || 'Fehler beim Ändern des Status.';
            toast.error(errorMsg);
        }
    };

    return (
        <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>
                Termine für "{event?.name}"
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{ position: 'absolute', right: 8, top: 8 }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {loading ? (
                    <Box display="flex" justifyContent="center" p={3}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <List>
                        {bookings.length === 0 ? (
                            <Typography align="center">Keine Termine gefunden.</Typography>
                        ) : (
                            bookings
                                .map((booking) => (
                                <ListItem key={booking._id} divider>
                                    <ListItemText
                                        primary={`Platz ${booking.courtId} | ${dayjs(booking.startTime).format('DD.MM.YYYY HH:mm')} - ${dayjs(booking.endTime).format('HH:mm')}`}
                                        secondary={booking.status === 1 ? <span style={{ color: 'red' }}>Storniert</span> : <span style={{ color: 'green' }}>Aktiv</span>}
                                    />
                                    <ListItemSecondaryAction>
                                        <IconButton 
                                            edge="end" 
                                            aria-label="toggle"
                                            onClick={() => handleToggleStatus(booking)}
                                            color={booking.status === 1 ? 'success' : 'error'}
                                            title={booking.status === 1 ? "Aktivieren" : "Deaktivieren"}
                                            disabled={dayjs(booking.startTime).isBefore(dayjs())} 
                                        >
                                            {booking.status === 1 ? <CheckCircleIcon /> : <BlockIcon />}
                                        </IconButton>
                                    </ListItemSecondaryAction>
                                </ListItem>
                            ))
                        )}
                    </List>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Schließen</Button>
            </DialogActions>
        </Dialog>
    );
};

export default EventBookingsModal;
